import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Button, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

export type NumberControlProps = {
    value: number;
    onChange: (value: number) => void;
    min: number | null;
    max: number | null;
    disabled?: boolean;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">;

const Wrapper = styled("div")(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
}));

const ButtonStyled = styled(Button)(({ theme }) => ({
    flexShrink: 0,
    flexGrow: 0,
    minWidth: 0,
    width: 40,
    height: 40,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.roundness,
}));

const Value = styled(Typography)({
    "&": {
        width: 24,
        textAlign: "center",
    },
});

export function NumberControl({ value, min, max, onChange, disabled, ...props }: NumberControlProps) {
    min = min ?? 0;
    max = max ?? Number.POSITIVE_INFINITY;
    if (min > max) {
        throw new Error(`NumberControl "min" prop (${min}) cannot be higher than "max" prop (${max}).`);
    }

    const changeValue = (delta: number) => {
        const newValue = value + delta;
        let finalValue: number;

        if (delta > 0) {
            // if we're changing in a positive direction
            // jump at least to the min value
            finalValue = Math.min(Math.max(min, newValue), max);
        } else {
            // if we are going in a negative direction
            finalValue =
                // and new value is below the minimum
                newValue < min
                    ? // got to zero
                      0
                    : // otherwise got to new value
                      newValue;
        }

        if (finalValue < min || finalValue > max) {
            return;
        }

        if (finalValue !== value) {
            onChange(finalValue);
        }
    };

    return (
        <Wrapper {...props}>
            <ButtonStyled variant="outlined" size="small" disabled={disabled} onClick={() => changeValue(-1)}>
                <RemoveIcon fontSize="small" />
            </ButtonStyled>
            <Value>{value}</Value>
            <ButtonStyled variant="outlined" size="small" disabled={disabled} onClick={() => changeValue(+1)}>
                <AddIcon fontSize="small" />
            </ButtonStyled>
        </Wrapper>
    );
}
